/** Product analytics (`acp.products|prod|season|ws|lostp`). `acpProductsEn` is typed against the Polish shape. */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const acpProductsPl = {
  products: {
    title: 'Analiza produktów',
    subtitle:
      'Co sprzedaje się najlepiej, co zarabia, jak działa sezon i gdzie leży niewykorzystany potencjał.',
    tab: {
      overview: 'Przegląd',
      margin: 'Marża × wolumen',
      season: 'Sezonowość',
      whitespace: 'White space i koszyk',
      lost: 'Utracone produkty',
      forecast: 'Prognoza zakupów',
    },
  },
  prod: {
    kpiSection: 'Wskaźniki produktów',
    kpi: {
      sales: 'Sprzedaż netto',
      margin: 'Marża',
      sold: 'Sprzedane produkty',
      soldHint: 'ze sprzedażą w okresie',
      unsold: 'Bez sprzedaży',
      unsoldHint: 'aktywne w katalogu, zero sprzedaży',
    },
    top: {
      title: 'Najlepsze i najsłabsze produkty',
      subtitleTop: 'Najlepsze wg wybranej miary · marża % i zmiana vs poprzedni okres',
      subtitleFlop: 'Najsłabsze spośród produktów ze sprzedażą · marża kwotowa bywa ujemna',
      top: 'Top',
      flop: 'Flop',
      net: 'Sprzedaż',
      margin: 'Marża',
      volume: 'Wolumen',
    },
    abc: {
      title: 'ABC produktów',
      subtitle: 'A = produkty do 80% sprzedaży, B = do 95%, C = reszta',
      products: 'produktów',
    },
    groups: {
      title: 'Struktura grup towarowych',
      subtitle: 'Udział w sprzedaży, marża i zmiana vs poprzedni okres',
      products: '{n} produktów',
    },
    table: {
      title: 'Wszystkie produkty i rotacja',
      subtitle: '{n} produktów · rotacja = faktur z produktem na tydzień',
      product: 'Produkt',
      margin: 'Marża zł',
      qty: 'Wolumen',
      customers: 'Klienci',
      rotation: 'Rotacja / tydz.',
      interval: 'Śr. odstęp',
    },
    quad: { STARS: 'Gwiazdy', CASH_COWS: 'Krowy dojne', QUESTION_MARKS: 'Znaki zapytania', DOGS: 'Psy' },
    quadDesc: {
      STARS: 'Duży wolumen i wysoka marża — chronić i rozwijać',
      CASH_COWS: 'Duży wolumen, niska marża — pilnować cen i kosztów',
      QUESTION_MARKS: 'Mały wolumen, wysoka marża — potencjał do rozwinięcia',
      DOGS: 'Mały wolumen, niska marża — kandydaci do wycofania',
    },
    mv: {
      quadSection: 'Ćwiartki marża × wolumen',
      title: 'Marża × wolumen',
      subtitle: 'Każdy punkt to produkt · wielkość = sprzedaż · kliknij punkt, by zaznaczyć go w tabeli',
      byVolume: 'Wolumen',
      byNet: 'Sprzedaż',
      median: 'Mediana',
      avg: 'Średnia',
      xVolume: 'Wolumen (skala log.)',
      xNet: 'Sprzedaż netto (skala log.)',
      yMargin: 'Marża %',
      hint: 'Linie przerywane = progi podziału: oś X ({x}) i marża — {m} z produktów.',
      products: '{n} prod.',
      share: 'Udział',
      quadrant: 'Ćwiartka',
      tableTitle: 'Produkty na wykresie',
      tableSubtitle: '{n} produktów · od największej sprzedaży',
    },
  },
  season: {
    heat: {
      title: 'Sezonowość tygodniowa',
      subtitle:
        'Indeks sezonowości tygodnia ISO względem średniej grupy · {weeks} tygodni danych · kliknij grupę, by zobaczyć miesiące',
      week: 'tydz. {w}',
      weekShort: 'tydz.',
      peakShort: 'szczyt t.',
      index: 'Indeks',
      vsAvg: 'vs średnia',
      peak: 'Szczyt sezonu',
      low: 'Dołek sezonu',
      now: 'Bieżący tydzień',
      above: 'powyżej średniej',
      below: 'poniżej średniej',
    },
    lines: {
      title: 'Profil miesięczny rok do roku',
      subtitle: 'Grupa: {group} · każdy rok jako osobna linia',
      group: 'Grupa towarowa',
      net: 'Sprzedaż',
      index: 'Indeks',
      inProgress: 'miesiąc w toku',
      average: 'średnia = 1,00',
    },
  },
  ws: {
    kpiSection: 'Wskaźniki white space',
    perMonth: 'mies.',
    perMonthShort: 'mies.',
    kpi: {
      potential: 'Potencjał dosprzedaży',
      potentialHint: 'netto / mies. · ok. {y} rocznie',
      gaps: 'Klienci z lukami',
      gapsHint: 'kupują tylko część asortymentu grupy',
      orders: 'Faktur w analizie koszyka',
      ordersHint: 'ostatnie 12 miesięcy',
      bestLift: 'Najsilniejsza para',
    },
    matrix: {
      title: 'Pokrycie asortymentu: klient × grupa',
      subtitle: 'Ile produktów z grupy klient kupuje (12 mies.) · pomarańczowa kropka = luka z potencjałem',
      top: 'TOP {n}',
      potential: 'Potencjał / mies.',
      legendLow: 'mało',
      legendHigh: 'pełny asortyment',
      legendGap: 'luka z potencjałem',
      footer: 'Klientów z lukami: {n}',
    },
    tip: {
      range: 'Asortyment',
      net: 'Sprzedaż 12 mies.',
      peers: 'Mediana rówieśników',
      potential: 'Potencjał',
      noGap: 'Brak luki względem rówieśników',
    },
    reps: {
      title: 'Szanse per handlowiec',
      subtitle: 'Szacowany potencjał dosprzedaży wg obecnego opiekuna · rozwiń, by zobaczyć największe luki',
      unassigned: 'Bez opiekuna',
      gaps: '{gaps} z {total} klientów z lukami',
    },
    basket: {
      title: 'Produkty kupowane razem',
      subtitle: 'Pary produktów wg lift · {n} faktur · próg wsparcia {s}',
      none: 'Brak par spełniających próg wsparcia.',
      orders: '{n} faktur',
      support: 'wsparcie {v}',
      conf: 'P(B|A) {ab} · P(A|B) {ba}',
      lift: 'lift',
      hint: 'Lift 3× = para pojawia się na fakturach trzy razy częściej, niż wynikałoby z przypadku. Dobry pomysł na ofertę łączoną.',
    },
  },
  lostp: {
    kpiSection: 'Wskaźniki utraconych produktów',
    kpi: {
      products: 'Produkty z utratą',
      productsHint: 'kupowane regularnie, a potem przestały',
      customers: 'Klienci, którzy przestali',
      customersHint: 'przynajmniej jeden regularny produkt',
      lost: 'Szac. utracona sprzedaż',
      lostHint: 'netto / mies. · ok. {y} rocznie',
    },
    title: 'Utracone produkty',
    subtitle: 'Reguła: min. {n} zakupów i przerwa dłuższa niż {f}× typowy odstęp (nie mniej niż {d} dni)',
    none: 'Brak utraconych produktów — klienci kupują regularnie.',
    col: { customers: 'Klientów', lost: 'Utracona sprzedaż / mies.', days: 'Śr. bez zakupu' },
    customers: 'Najwięksi klienci, którzy przestali kupować',
    lastBought: 'ostatnio {date} ({days} dni temu)',
    typically: 'zwykle co {n} dni',
    more: 'Pokaż więcej',
  },
  fcst: {
    note: 'Popyt wg daty wydania (WZ) - kiedy towar był potrzebny, niezależnie od faktur zbiorczych. Historia: 36 pełnych miesięcy do {date}.',
    kpi: {
      next: 'Prognoza na {m}',
      nextHint: 'rok temu {v}',
      products: 'Towary z popytem',
      productsHint: 'sprzedawane w ostatnich 12 mies.',
      accuracy: 'Trafność modelu',
      accuracyHint: 'cała sprzedaż, 1 mies. w przód, 6 ostatnich mies. · pojedyncze towary: {products}',
    },
    highlights: 'Najważniejsze dla zakupów',
    highlightsHint: 'Wyliczone z liczb poniżej, bez wysyłania danych do modelu AI.',
    chart: 'Ilość: historia i prognoza',
    chartHint: 'Kliknij towar w tabeli. Słupki jasne = prognoza, linia = ten sam miesiąc rok wcześniej.',
    history: 'Wydano',
    forecast: 'Prognoza',
    lastYear: 'Rok wcześniej',
    mtd: 'w toku: {v}',
    table: 'Prognoza na towar',
    tableHint: 'Poziom z 3 ostatnich miesięcy oczyszczony z sezonu × indeks sezonowy miesiąca (historia od 2023; towar bez dwóch lat regularnej sprzedaży bierze sezon swojej grupy), uzgodnione z prognozą całej sprzedaży. Najwyżej 1,25 × najlepszy miesiąc z dwóch lat; bieżący miesiąc = już wydane + prognoza na pozostałe dni.',
    col: { product: 'Towar', avg3: 'Śr. 3 mies.', trend: 'Trend r/r', accuracy: 'Trafność', peak: 'Szczyt' },
    sort: { forecastNet: 'Wartość', season: 'Sezon', trend: 'Trend', accuracy: 'Trafność' },
    horizon: 'Horyzont',
    months: '{n} mies.',
    method: { SEASONAL: 'sezon towaru', GROUP: 'sezon grupy (krótka historia towaru)', AVERAGE: 'średnia (krótka historia)' },
    index: 'indeks {v}',
    prev: 'Poprzednie',
    next: 'Następne',
    page: '{from}–{to} z {total}',
    none: 'Brak sprzedaży w wybranym zakresie - nie ma czego prognozować.',
    noneFiltered: 'Żaden towar nie pasuje do filtrów tabeli.',
    strip: {
      title: 'Prognoza popytu na kolejne miesiące',
      current: 'Bieżący miesiąc',
      next: 'Najbliższe zakupy',
      later: 'Plan',
      lastYear: 'rok temu {v}',
      delivered: 'wydano {v} · {p} prognozy',
      noLastYear: 'brak porównania r/r',
    },
    acc: {
      title: 'Trafność modelu',
      total: 'cała sprzedaż',
      products: 'pojedyncze towary',
      hint: 'prognoza 1 mies. w przód, 6 ostatnich miesięcy',
    },
    total: {
      title: 'Popyt w czasie',
      hint: 'Wartość netto wg daty wydania (WZ): 12 miesięcy historii i prognoza. Linia przerywana = te same miesiące rok wcześniej.',
      zone: 'prognoza',
    },
    signals: {
      title: 'Sygnały dla kupca',
      hint: 'Spośród największych wartościowo towarów, na {m}. Kliknij towar, by zobaczyć szczegóły.',
      seasonUp: 'Wchodzą w sezon',
      seasonDown: 'Schodzą z sezonu',
      growing: 'Rosną r/r',
      falling: 'Spadają r/r',
      none: 'nic wyraźnego',
    },
    groups: {
      title: 'Grupy towarowe',
      hint: 'Prognoza na {m} i zmiana do roku wcześniej.',
      hintClick: 'Prognoza na {m} i zmiana do roku wcześniej. Kliknij grupę, by zawęzić widok.',
      none: 'Bez grupy',
      products: '{n} tow.',
      more: 'Pokaż wszystkie ({n})',
      less: 'Pokaż mniej',
    },
    seg: { all: 'Wszystkie', seasonUp: 'Wchodzą w sezon', seasonDown: 'Schodzą z sezonu', growing: 'Rosną', falling: 'Spadają', uncertain: 'Niepewne' },
    segHint: {
      all: 'Wszystkie towary z popytem',
      seasonUp: 'Indeks sezonu następnego miesiąca ≥ 1,25',
      seasonDown: 'Indeks sezonu następnego miesiąca ≤ 0,75',
      growing: 'Ostatnie 3 miesiące ≥ +25% r/r',
      falling: 'Ostatnie 3 miesiące ≤ −25% r/r',
      uncertain: 'Trafność poniżej 50% albo brak historii - planuj z zapasem lub ręcznie',
    },
    search: 'Szukaj towaru lub symbolu…',
    export: 'Eksport XLSX',
    exporting: 'Przygotowuję…',
    exportError: 'Nie udało się przygotować pliku.',
    tableCount: '{n} towarów',
    col2: { spark: '12 mies. + prognoza', season: 'Sezon sty–gru' },
    scn: {
      open: 'Scenariusz co-jeśli',
      title: 'Scenariusz „co jeśli”',
      hint: 'Skoryguj popyt całości i grup (np. promocja, nowy klient, pogoda) - liczby całego widoku i eksport XLSX przeliczą się od razu. Wydane w tym miesiącu zostaje bez zmian.',
      all: 'Cała sprzedaż',
      allHint: 'Korekta wszystkich towarów',
      groups: 'Grupy towarowe',
      groupsHint: 'Korekta grupy dokłada się do korekty całości (×).',
      moreGroups: 'Wszystkie grupy ({n})',
      lessGroups: 'Mniej grup',
      impact: 'Wpływ na {m}',
      impactHorizon: 'Cały horyzont',
      base: 'bez scenariusza {v}',
      reset: 'Wyczyść scenariusz',
      close: 'Zwiń',
      active: 'Scenariusz aktywny',
      edit: 'Zmień',
      everything: 'całość',
      decrease: 'Zmniejsz o 5 pp.',
      increase: 'Zwiększ o 5 pp.',
      exportNote: 'Eksport XLSX uwzględni scenariusz.',
      tag: 'scenariusz',
    },
    band: {
      label: 'Zakres niepewności',
      range: 'zakres {lo}–{hi}',
      hint: 'Szerokość z trafności modelu (6 ostatnich miesięcy), rośnie z odległością miesiąca.',
      base: 'Bez scenariusza',
    },
    seasonMap: {
      title: 'Mapa sezonu grup',
      hint: 'Indeks sezonu grupy w każdym miesiącu roku (ważony wartością towarów). Ciepły = sezon, chłodny = poza sezonem; obwódka = miesiące prognozy.',
      hintClick:
        'Indeks sezonu grupy w każdym miesiącu roku (ważony wartością towarów). Ciepły = sezon, chłodny = poza sezonem; obwódka = miesiące prognozy. Kliknij grupę, by zawęzić widok.',
      low: 'poza sezonem',
      high: 'sezon',
      value: 'Prognoza {m}',
    },
    bridge: {
      title: 'Co zmienia popyt r/r',
      hint: '{m}: od wartości sprzed roku, przez grupy, które zmieniły się najbardziej, do prognozy.',
      start: 'Rok temu',
      end: 'Prognoza',
      rest: 'Pozostałe grupy',
      none: 'Brak porównania z rokiem wcześniej.',
    },
    drawer: {
      weeks: 'Plan tygodniowy na {m}',
      weeksHint: 'Prognoza miesiąca rozłożona na tygodnie kalendarzowe wg liczby dni.',
      weekDays: '{n} dni',
      prevProduct: 'Poprzedni towar',
      nextProduct: 'Następny towar',
      position: '{i} z {n} · strzałki ← → przełączają towar',
      next: 'Prognoza na {m}',
      perWeek: 'Na tydzień',
      perWeekHint: 'średnio w {m}',
      trend: 'Trend r/r',
      trendHint: '3 ost. mies. vs rok wcześniej',
      accuracy: 'Trafność',
      accuracyHint: '6 ostatnich miesięcy',
      season: 'Sezon w roku',
      seasonHint: 'Indeks 1,00 = przeciętny miesiąc towaru. Ciepły kolor = sezon, chłodny = poza sezonem. Obwódka = miesiące prognozy.',
      peak: 'szczyt: {m}',
      noPeak: 'bez wyraźnego szczytu',
      months: 'Miesiąc po miesiącu',
      colMonth: 'Miesiąc',
      colForecast: 'Prognoza',
      colValue: 'Wartość',
      colLastYear: 'Rok temu',
      colIndex: 'Indeks',
      stock: 'Magazyn i zamówienia',
      stockHint: 'Jak w zakładce „Do zamówienia” (horyzont 14 dni).',
      stockNow: 'Stan',
      open: 'Zamówione',
      toOrder: 'Do zamówienia',
      cover: 'Pokrycie',
      coverDays: '{n} dni',
      supplier: 'Główny dostawca',
      lastPrice: 'Ostatnia cena zakupu',
      price: 'śr. cena sprzedaży netto {v}',
      noStock: 'Brak danych magazynowych dla tego towaru.',
      loading: 'Wczytuję…',
      close: 'Zamknij',
    },
  },
};

export type AcpProducts = typeof acpProductsPl;

export const acpProductsEn: Shape<AcpProducts> = {
  products: {
    title: 'Product analytics',
    subtitle: 'What sells best, what earns, how the season works and where the untapped potential is.',
    tab: {
      overview: 'Overview',
      margin: 'Margin × volume',
      season: 'Seasonality',
      whitespace: 'White space & basket',
      lost: 'Lost products',
      forecast: 'Purchase forecast',
    },
  },
  prod: {
    kpiSection: 'Product indicators',
    kpi: {
      sales: 'Net sales',
      margin: 'Margin',
      sold: 'Products sold',
      soldHint: 'with sales in the period',
      unsold: 'Without sales',
      unsoldHint: 'active in catalogue, zero sales',
    },
    top: {
      title: 'Best and weakest products',
      subtitleTop: 'Best by the chosen measure · margin % and change vs previous period',
      subtitleFlop: 'Weakest among products with sales · margin amount can be negative',
      top: 'Top',
      flop: 'Flop',
      net: 'Sales',
      margin: 'Margin',
      volume: 'Volume',
    },
    abc: {
      title: 'Product ABC',
      subtitle: 'A = products up to 80% of sales, B = up to 95%, C = the rest',
      products: 'products',
    },
    groups: {
      title: 'Product group mix',
      subtitle: 'Share of sales, margin and change vs previous period',
      products: '{n} products',
    },
    table: {
      title: 'All products & rotation',
      subtitle: '{n} products · rotation = invoices with the product per week',
      product: 'Product',
      margin: 'Margin PLN',
      qty: 'Volume',
      customers: 'Customers',
      rotation: 'Rotation / wk',
      interval: 'Avg interval',
    },
    quad: { STARS: 'Stars', CASH_COWS: 'Cash cows', QUESTION_MARKS: 'Question marks', DOGS: 'Dogs' },
    quadDesc: {
      STARS: 'High volume and high margin — protect and grow',
      CASH_COWS: 'High volume, low margin — watch prices and costs',
      QUESTION_MARKS: 'Low volume, high margin — room to grow',
      DOGS: 'Low volume, low margin — candidates to drop',
    },
    mv: {
      quadSection: 'Margin × volume quadrants',
      title: 'Margin × volume',
      subtitle: 'Each dot is a product · size = sales · click a dot to highlight it in the table',
      byVolume: 'Volume',
      byNet: 'Sales',
      median: 'Median',
      avg: 'Average',
      xVolume: 'Volume (log scale)',
      xNet: 'Net sales (log scale)',
      yMargin: 'Margin %',
      hint: 'Dashed lines = split thresholds: X axis ({x}) and margin — {m} of the products.',
      products: '{n} prod.',
      share: 'Share',
      quadrant: 'Quadrant',
      tableTitle: 'Products on the chart',
      tableSubtitle: '{n} products · biggest sales first',
    },
  },
  season: {
    heat: {
      title: 'Weekly seasonality',
      subtitle:
        'ISO-week seasonality index vs the group average · {weeks} weeks of data · click a group to see its months',
      week: 'wk {w}',
      weekShort: 'wk',
      peakShort: 'peak wk',
      index: 'Index',
      vsAvg: 'vs average',
      peak: 'Season peak',
      low: 'Season low',
      now: 'Current week',
      above: 'above average',
      below: 'below average',
    },
    lines: {
      title: 'Monthly profile, year over year',
      subtitle: 'Group: {group} · each year as its own line',
      group: 'Product group',
      net: 'Sales',
      index: 'Index',
      inProgress: 'month in progress',
      average: 'average = 1.00',
    },
  },
  ws: {
    kpiSection: 'White space indicators',
    perMonth: 'mo.',
    perMonthShort: 'mo.',
    kpi: {
      potential: 'Cross-sell potential',
      potentialHint: 'net / month · about {y} a year',
      gaps: 'Customers with gaps',
      gapsHint: 'buy only part of a group’s range',
      orders: 'Invoices in basket analysis',
      ordersHint: 'last 12 months',
      bestLift: 'Strongest pair',
    },
    matrix: {
      title: 'Range coverage: customer × group',
      subtitle:
        'How many of a group’s products the customer buys (12 mo.) · amber dot = a gap with potential',
      top: 'TOP {n}',
      potential: 'Potential / mo.',
      legendLow: 'little',
      legendHigh: 'full range',
      legendGap: 'gap with potential',
      footer: 'Customers with gaps: {n}',
    },
    tip: {
      range: 'Range',
      net: 'Sales 12 mo.',
      peers: 'Peer median',
      potential: 'Potential',
      noGap: 'No gap vs peers',
    },
    reps: {
      title: 'Opportunity per sales rep',
      subtitle: 'Estimated cross-sell potential by current account owner · expand to see the biggest gaps',
      unassigned: 'Unassigned',
      gaps: '{gaps} of {total} customers with gaps',
    },
    basket: {
      title: 'Products bought together',
      subtitle: 'Product pairs by lift · {n} invoices · support threshold {s}',
      none: 'No pairs meet the support threshold.',
      orders: '{n} invoices',
      support: 'support {v}',
      conf: 'P(B|A) {ab} · P(A|B) {ba}',
      lift: 'lift',
      hint: 'A lift of 3× means the pair shows up on invoices three times more often than by chance. A good bundle idea.',
    },
  },
  fcst: {
    note: 'Demand by delivery date (WZ) - when the goods were needed, regardless of collective invoices. History: 36 complete months to {date}.',
    kpi: {
      next: 'Forecast for {m}',
      nextHint: 'last year {v}',
      products: 'Products with demand',
      productsHint: 'sold in the last 12 months',
      accuracy: 'Model accuracy',
      accuracyHint: 'total sales, one month ahead, last 6 months · single products: {products}',
    },
    highlights: 'Key points for purchasing',
    highlightsHint: 'Computed from the numbers below, no data sent to an AI model.',
    chart: 'Quantity: history and forecast',
    chartHint: 'Click a product in the table. Light bars = forecast, line = the same month a year earlier.',
    history: 'Delivered',
    forecast: 'Forecast',
    lastYear: 'Year earlier',
    mtd: 'in progress: {v}',
    table: 'Forecast per product',
    tableHint: 'Level of the last 3 months without the season × seasonal index of the month (history since 2023; a product without two years of regular sales uses its group season), reconciled with the total sales forecast. At most 1.25 × the best month of two years; the current month = delivered so far + the forecast for the days left.',
    col: { product: 'Product', avg3: 'Avg 3 mo.', trend: 'Trend YoY', accuracy: 'Accuracy', peak: 'Peak' },
    sort: { forecastNet: 'Value', season: 'Season', trend: 'Trend', accuracy: 'Accuracy' },
    horizon: 'Horizon',
    months: '{n} mo.',
    method: { SEASONAL: 'own season', GROUP: 'group season (short product history)', AVERAGE: 'average (short history)' },
    index: 'index {v}',
    prev: 'Previous',
    next: 'Next',
    page: '{from}–{to} of {total}',
    none: 'No sales in the selected scope - nothing to forecast.',
    noneFiltered: 'No product matches the table filters.',
    strip: {
      title: 'Demand forecast for the coming months',
      current: 'Current month',
      next: 'Next purchases',
      later: 'Plan',
      lastYear: 'last year {v}',
      delivered: 'delivered {v} · {p} of forecast',
      noLastYear: 'no year-on-year base',
    },
    acc: {
      title: 'Model accuracy',
      total: 'total sales',
      products: 'single products',
      hint: 'one month ahead, last 6 months',
    },
    total: {
      title: 'Demand over time',
      hint: 'Net value by delivery date (WZ): 12 months of history and the forecast. Dashed line = the same months a year earlier.',
      zone: 'forecast',
    },
    signals: {
      title: 'Signals for the buyer',
      hint: 'Among the products with the highest value, for {m}. Click a product for details.',
      seasonUp: 'Entering their season',
      seasonDown: 'Leaving their season',
      growing: 'Growing YoY',
      falling: 'Falling YoY',
      none: 'nothing notable',
    },
    groups: {
      title: 'Product groups',
      hint: 'Forecast for {m} and the change to a year earlier.',
      hintClick: 'Forecast for {m} and the change to a year earlier. Click a group to narrow the view.',
      none: 'No group',
      products: '{n} prod.',
      more: 'Show all ({n})',
      less: 'Show less',
    },
    seg: { all: 'All', seasonUp: 'Season starts', seasonDown: 'Season ends', growing: 'Growing', falling: 'Falling', uncertain: 'Uncertain' },
    segHint: {
      all: 'All products with demand',
      seasonUp: 'Seasonal index of the next month ≥ 1.25',
      seasonDown: 'Seasonal index of the next month ≤ 0.75',
      growing: 'Last 3 months ≥ +25% YoY',
      falling: 'Last 3 months ≤ −25% YoY',
      uncertain: 'Accuracy below 50% or no history - plan with a buffer or by hand',
    },
    search: 'Search product or symbol…',
    export: 'Export XLSX',
    exporting: 'Preparing…',
    exportError: 'Could not prepare the file.',
    tableCount: '{n} products',
    col2: { spark: '12 mo. + forecast', season: 'Season Jan–Dec' },
    scn: {
      open: 'What-if scenario',
      title: 'What-if scenario',
      hint: 'Adjust the demand of the whole and of groups (promotion, a new customer, weather) - the whole view and the XLSX export recalculate at once. What is already delivered this month stays.',
      all: 'All sales',
      allHint: 'Correction of every product',
      groups: 'Product groups',
      groupsHint: 'A group correction multiplies with the overall one (×).',
      moreGroups: 'All groups ({n})',
      lessGroups: 'Fewer groups',
      impact: 'Impact on {m}',
      impactHorizon: 'Whole horizon',
      base: 'without scenario {v}',
      reset: 'Clear scenario',
      close: 'Collapse',
      active: 'Scenario active',
      edit: 'Change',
      everything: 'all',
      decrease: 'Decrease by 5 pp',
      increase: 'Increase by 5 pp',
      exportNote: 'The XLSX export includes the scenario.',
      tag: 'scenario',
    },
    band: {
      label: 'Uncertainty range',
      range: 'range {lo}–{hi}',
      hint: 'Width from the model accuracy (last 6 months), growing with how far ahead the month is.',
      base: 'Without scenario',
    },
    seasonMap: {
      title: 'Group season map',
      hint: 'Season index of the group in every month (weighted by product value). Warm = season, cool = off-season; outline = forecast months.',
      hintClick:
        'Season index of the group in every month (weighted by product value). Warm = season, cool = off-season; outline = forecast months. Click a group to narrow the view.',
      low: 'off-season',
      high: 'season',
      value: 'Forecast {m}',
    },
    bridge: {
      title: 'What changes demand y/y',
      hint: '{m}: from last year, through the groups that changed most, to the forecast.',
      start: 'Last year',
      end: 'Forecast',
      rest: 'Other groups',
      none: 'No comparison with a year earlier.',
    },
    drawer: {
      weeks: 'Weekly plan for {m}',
      weeksHint: 'The month forecast split into calendar weeks by the number of days.',
      weekDays: '{n} days',
      prevProduct: 'Previous product',
      nextProduct: 'Next product',
      position: '{i} of {n} · arrow keys ← → switch the product',
      next: 'Forecast for {m}',
      perWeek: 'Per week',
      perWeekHint: 'average in {m}',
      trend: 'Trend YoY',
      trendHint: 'last 3 mo. vs a year earlier',
      accuracy: 'Accuracy',
      accuracyHint: 'last 6 months',
      season: 'Season over the year',
      seasonHint: 'Index 1.00 = an average month of the product. Warm = season, cool = off-season. Outline = forecast months.',
      peak: 'peak: {m}',
      noPeak: 'no clear peak',
      months: 'Month by month',
      colMonth: 'Month',
      colForecast: 'Forecast',
      colValue: 'Value',
      colLastYear: 'Last year',
      colIndex: 'Index',
      stock: 'Stock and orders',
      stockHint: 'As in the "To order" tab (14-day horizon).',
      stockNow: 'Stock',
      open: 'Ordered',
      toOrder: 'To order',
      cover: 'Cover',
      coverDays: '{n} days',
      supplier: 'Main supplier',
      lastPrice: 'Last purchase price',
      price: 'avg net sales price {v}',
      noStock: 'No stock data for this product.',
      loading: 'Loading…',
      close: 'Close',
    },
  },
  lostp: {
    kpiSection: 'Lost product indicators',
    kpi: {
      products: 'Products with losses',
      productsHint: 'bought regularly, then stopped',
      customers: 'Customers who stopped',
      customersHint: 'at least one regular product',
      lost: 'Est. lost sales',
      lostHint: 'net / month · about {y} a year',
    },
    title: 'Lost products',
    subtitle: 'Rule: min. {n} purchases and a gap longer than {f}× the typical interval (at least {d} days)',
    none: 'No lost products — customers keep buying regularly.',
    col: { customers: 'Customers', lost: 'Lost sales / mo.', days: 'Avg since purchase' },
    customers: 'Biggest customers who stopped buying',
    lastBought: 'last on {date} ({days} days ago)',
    typically: 'usually every {n} days',
    more: 'Show more',
  },
};
